import { notification, message, FormInstance } from "antd";
import { CustomButton, CustomModal } from "@/shared/ui";
import i18nInstance from "@/i18n";
import { useGlobalError } from "@/shared/hooks";
import { translateBackendError } from "./translateError";
import { normalizeApiError } from "./normalizeApiError";
import type { NormalizedError } from "./normalizeApiError";

interface ApiErrorItem {
  field: string | string[];
  message: string | string[];
}

interface ApiError {
  message?: string;
  code?: string;
  config?: { url?: string };
  response?: {
    status?: number;
    data?: {
      title?: string;
      message?: string;
      statusCode?: number;
      timestamp?: string;
      errors?: ApiErrorItem[];
    };
  };
}

interface FormFinishFailedInfo {
  errorFields: { name: (string | number)[]; errors: string[] }[];
}

/** Billing route for the store currently in the URL (no router access here). */
const billingPath = (): string => {
  const match = window.location.pathname.match(/^\/store\/([^/]+)/);
  return match ? `/store/${match[1]}/billing` : "/billing";
};

/**
 * Business-rule violations (plan caps, "can't revert twice", …) are not form
 * problems — titling them "Please check the form for errors" told vendors to
 * fix a field when nothing on the form was wrong. This global path owns
 * toasts, so it presents the rule itself; components must not add their own
 * (that produced two notifications for one error).
 *
 * Returns true when it handled the error.
 */
const notifyBusinessRule = (normalized: NormalizedError): boolean => {
  if (normalized.errorType !== "BUSINESS_RULE") return false;

  if (normalized.businessRuleType === "PLAN_LIMIT_EXCEEDED") {
    notification.warning({
      message: i18nInstance.t("errors.planLimitReached"),
      description: (
        <div>
          <p style={{ marginBottom: 12 }}>{normalized.message}</p>
          <CustomButton
            type="primary"
            size="small"
            onClick={() => {
              notification.destroy();
              window.location.assign(billingPath());
            }}
          >
            {i18nInstance.t("errors.upgradePlan")}
          </CustomButton>
        </div>
      ),
      // Sticky: the vendor needs to act (upgrade), not watch it disappear.
      duration: 0,
    });
    return true;
  }

  notification.warning({
    message: i18nInstance.t("errors.actionNotAllowed"),
    description: normalized.message,
    duration: 6,
  });
  return true;
};

/** A plain error message, for the cases that aren't an axios failure. */
export interface ErrorToast {
  title: string;
  description?: string;
}

/**
 * The error toast. Counterpart to `handleSuccess` — same shape, same duration
 * family, so a failure looks like the mirror of a success rather than a
 * different library's idea of a message.
 *
 * Takes either an axios error (normalised, field errors expanded, business
 * rules routed to their own treatment) OR a plain string / { title,
 * description } for the failures that never went near the network — a failed
 * file read, a client-side guard, a clipboard write.
 *
 * That second form is why this is exported. 23 call sites were reaching past
 * it for `message.error` and `notification.error` directly, which meant two
 * visual styles and two default durations for the same event.
 */
export const notifyError = (error: ApiError | string | ErrorToast) => {
  notification.destroy();

  // Plain message — nothing to normalise.
  if (typeof error === "string" || (error && !("response" in error) && "title" in error)) {
    const toast: ErrorToast =
      typeof error === "string" ? { title: error } : (error as ErrorToast);
    return notification.error({
      message: toast.title,
      description: toast.description,
      duration: 5,
    });
  }

  const normalized = normalizeApiError(error as ApiError);
  if (notifyBusinessRule(normalized)) return;

  // Field-level validation — "check the form" is the right instruction only
  // when at least one real field is at fault. A payload carrying nothing but
  // `general` entries falls through to the titled toast below.
  if (normalized.fieldErrors.size > 0) {
    // Body keeps every message the payload carried — field entries plus any
    // `general` ones riding along — so nothing the backend said is dropped.
    const msg = [
      ...Array.from(normalized.fieldErrors.values()).flat(),
      ...normalized.generalErrors,
    ].join("\n");

    return notification.error({
      message: i18nInstance.t("errors.validationError"),
      description: <div className="whitespace-pre-wrap">{msg}</div>,
      duration: 6,
    });
  }

  const apiError = error as ApiError;
  // A 4xx is the server refusing what was sent, and it will refuse the same
  // thing again — titling it "Please try again" sent merchants round in a
  // loop (13 Oct: a duplicate variant SKU, where the body already said exactly
  // what to change). Only a 5xx, where a retry can genuinely work, keeps it.
  const status = apiError?.response?.status ?? 0;
  const fallbackTitle =
    status >= 400 && status < 500
      ? i18nInstance.t("errors.notCompleted")
      : i18nInstance.t("errors.generic");
  return notification.error({
    message: apiError?.response?.data?.title || fallbackTitle,
    description:
      normalized.generalErrors[0] ||
      apiError?.response?.data?.message ||
      "An unexpected error occurred. Please try again.",
    duration: 5,
  });
};

/**
 * The lighter error toast — a strip, not a card. Mirror of
 * `handleSuccessMessage`, and the counterpart to `notifyError` for failures
 * that don't warrant a titled card ("Copy failed", "That's not an image").
 *
 * Exported for the same reason `notifyError` is: call sites were reaching for
 * `message.error` directly, which is the same widget with none of the
 * translation or the destroy-previous behaviour.
 */
export const notifyErrorMessage = (error: ApiError | string) => {
  message.destroy();
  if (typeof error === "string") return message.error(error);
  return message.error(
    translateBackendError(error, (k) => i18nInstance.t(k) as string),
  );
};

/**
 * The warning toast — and the reason it exists.
 *
 * The helper set modelled success and error, each in both weights (card and
 * strip), but had nothing for "this is allowed, but read it first": a duplicate
 * option value, a name already taken, a limit about to be hit. So eight call
 * sites reached for `notification.warning` directly and picked their own
 * duration — 3s in variant-management, 4s in PresetFormDrawer, 6s in beta
 * access. Same class of event, three lifetimes, because there was no helper to
 * agree with.
 *
 * 5s matches `notifyError`: a warning has to be read to be useful, so it gets
 * the reading time an error gets rather than a success's glanceable 4s.
 */
export const notifyWarning = (warning: string | ErrorToast) => {
  notification.destroy();
  const toast: ErrorToast =
    typeof warning === "string" ? { title: warning } : warning;
  return notification.warning({
    message: toast.title,
    description: toast.description,
    duration: 5,
  });
};

/** The strip form of {@link notifyWarning}, for warnings not worth a card. */
export const notifyWarningMessage = (warning: string) => {
  message.destroy();
  return message.warning(warning);
};

/**
 * Handles form errors from API responses and scrolls to the first error field
 * @param error - The error object from API response
 * @param form - Ant Design form instance
 */
export const handleFormError = (error: ApiError, form: FormInstance) => {
  // Check if there are errors in the response
  if (
    Array.isArray(error?.response?.data?.errors) &&
    error?.response?.data?.errors.length > 0
  ) {
    let firstErrorField: string | null = null;

    // Iterate through the errors
    error.response?.data?.errors.forEach((item: ApiErrorItem, index: number) => {
      // "general" / non-field errors are handled by the global mutation
      // error handler in queryClient.ts (handleMutationError → notifyError),
      // which already surfaces a toast with the BE's `errors[].message`.
      // Showing it here too would produce two duplicate notifications
      // for the same error (e.g. trial-plan "staff limit reached" toast
      // appearing twice — once as "Please check the form for errors" +
      // once as the actual cap message). This handler's job is to set
      // FIELD-level errors on the form; the global path owns toasts.
      if (item.field === "general" || item.field?.includes("general")) {
        return;
      }

      // Store the first error field name for scrolling
      if (index === 0 && !firstErrorField) {
        firstErrorField = Array.isArray(item.field)
          ? item.field[0]
          : item.field;
      }

      // Handle both string and array field formats
      const fields = Array.isArray(item.field) ? item.field : [item.field];
      const messages = Array.isArray(item.message)
        ? item.message
        : [item.message];

      // Set form fields for field-level errors (e.g. "email is invalid")
      form?.setFields(
        fields.map((field: string) => ({
          name: field,
          errors: messages,
        })),
      );
    });

    // Scroll to the first error field
    if (firstErrorField) {
      // Use setTimeout to ensure the error is set before scrolling
      setTimeout(() => {
        form?.scrollToField(firstErrorField as string, {
          behavior: "smooth",
          block: "center",
          scrollMode: "if-needed",
        });
      }, 100);
    }
  }
};

/**
 * Handler for Ant Design Form's onFinishFailed event
 * Automatically scrolls to the first field with validation error
 * @param errorInfo - Error information from form validation
 */
export const onFormFinishFailed = (errorInfo: FormFinishFailedInfo) => {
  const { errorFields } = errorInfo;
  if (!errorFields?.length) return;

  const firstErrorField = errorFields[0].name;
  const fieldId = Array.isArray(firstErrorField)
    ? firstErrorField.join("_")
    : firstErrorField;

  // Deferred a tick so the error state has rendered — the fallback below
  // looks for antd's has-error class, which is not on the DOM yet when
  // onFinishFailed fires.
  setTimeout(() => {
    const fieldElement =
      document.querySelector(`[id="${fieldId}"]`) ||
      document.querySelector(`[name="${fieldId}"]`);

    // Scroll to the whole Form.Item row (label + control + error text), not
    // the control: many controls put their id on an element with no box —
    // an Upload's display:none file input — and scrolling to that is a
    // silent no-op, so a failed save looked like a dead button.
    const target =
      fieldElement?.closest(".ant-form-item") ??
      fieldElement ??
      document.querySelector(".ant-form-item-has-error");
    if (!target) return;

    target.scrollIntoView({ behavior: "smooth", block: "center" });

    if (fieldElement instanceof HTMLElement) {
      setTimeout(() => fieldElement.focus({ preventScroll: true }), 300);
    }
  }, 0);
};

export const handleError = (error: ApiError) => {
  // eslint-disable-next-line no-console
  if (import.meta.env.DEV) console.log("[handleError] Called with error:", {
    status: error?.response?.status,
    message: error?.message,
    code: error?.code,
    hasResponse: !!error?.response,
  });

  // Detect request timeout (axios ECONNABORTED + "timeout" in message)
  const isTimeout =
    error?.code === "ECONNABORTED" ||
    error?.code === "ERR_CANCELED" ||
    (error?.message?.toLowerCase().includes("timeout") ?? false);

  if (isTimeout) {
    const { setError } = useGlobalError.getState();
    setError(
      0,
      "Server is not responding.",
      "The request took too long to complete. The server may be down or overloaded. Please try again.",
      {
        type: "TIMEOUT",
        message: error?.message || "Request timed out",
        code: error?.code || "ECONNABORTED",
        path: window.location.pathname,
        endpoint: error?.config?.url || "N/A",
        timestamp: new Date().toISOString(),
      },
    );
    return;
  }

  // Detect network/server unreachable errors
  const isNetworkError =
    !navigator.onLine || // Browser is offline
    error?.message === "Network Error" || // Axios network error
    error?.code === "ERR_NETWORK" || // Network error code
    !error?.response || // No response received from server
    error?.response?.status === 0; // Status 0 indicates network failure

  if (isNetworkError) {
    const { setError } = useGlobalError.getState();
    setError(
      0,
      "You're offline.",
      "We can't reach the server. Check your internet connection and try again.",
      {
        type: "NETWORK_ERROR",
        message: error?.message || "Network Error",
        code: error?.code || "UNKNOWN",
        path: window.location.pathname,
        endpoint: error?.config?.url || "N/A",
        timestamp: new Date().toISOString(),
      },
    );
    return;
  }

  // 401 — already handled silently by the axios interceptor (token refresh / redirect)
  if (error?.response?.status === 401) {
    return;
  }

  // 440 session expired — show modal prompt regardless of query or mutation context
  if (
    error?.response?.status === 440 &&
    window.location.pathname !== "/login"
  ) {
    return CustomModal.confirm({
      title: i18nInstance.t("auth.sessionExpired"),
      content: (
        <div className="flex flex-col text-white">
          <div>{i18nInstance.t("messages.unsavedChanges")}</div>
          <div className="flex justify-end">
            <CustomButton
              type="primary"
              size="large"
              onClick={() => {
                localStorage.removeItem("accessToken");
                localStorage.removeItem("refreshToken");
                window.location.href = "/";
              }}
              dataCy="logout-confirm-btn"
            >
              {i18nInstance.t("actions.confirm")}
            </CustomButton>
          </div>
        </div>
      ),
      footer: false,
    });
  }

  // 404 / 403 / 5xx — do NOT trigger a full-page error overlay.
  // These are section-level API failures. The calling component's `isError`
  // flag will be true and it should render a <SectionError> inline.
  // Full-page 404/403 errors must be triggered explicitly by the page
  // component itself (e.g. when the primary resource of a detail page is missing).
};

export const handleErrorMessage = (error: ApiError) => {
  notifyErrorMessage(error);
};

/**
 * Used for mutation errors (create / update / delete actions).
 * Always resolves with a toast — never triggers a full-page error overlay.
 * Network and timeout errors are described specifically; all HTTP errors
 * fall through to the generic notifyError toast.
 */
export const handleMutationError = (error: ApiError) => {
  const isTimeout =
    error?.code === "ECONNABORTED" ||
    error?.code === "ERR_CANCELED" ||
    (error?.message?.toLowerCase().includes("timeout") ?? false);

  if (isTimeout) {
    notification.error({
      message: "Request timed out",
      description: "The server took too long to respond. Please try again.",
      duration: 6,
    });
    return;
  }

  // Pre-flight client-side errors (e.g. credential encryption failed before
  // the HTTP request fires). These shouldn't be misclassified as "no
  // connection" — they have no `.response` because they never reached the
  // network, but they're a config problem, not a network problem.
  const errName = (error as { name?: string } | undefined)?.name;
  const errCode = (error as { code?: string } | undefined)?.code;
  if (errName === "CredentialEncryptionError") {
    const codeMap: Record<string, { title: string; body: string }> = {
      MISSING_TRANSIT_KEY: {
        title: "Encryption not configured",
        body: "Credentials can't be encrypted because the dashboard was built without a transit key. Set VITE_CREDENTIAL_TRANSIT_KEY in the production build and redeploy.",
      },
      CRYPTO_UNAVAILABLE: {
        title: "Secure connection required",
        body: "Credentials can only be encrypted over HTTPS. Reach the dashboard via its HTTPS URL and try again.",
      },
      ENCRYPT_FAILED: {
        title: "Couldn't encrypt credentials",
        body:
          (error as { message?: string } | undefined)?.message ??
          "An unexpected error happened while encrypting your credentials.",
      },
    };
    const copy =
      (errCode && codeMap[errCode]) ?? codeMap.ENCRYPT_FAILED;
    notification.error({
      message: copy.title,
      description: copy.body,
      duration: 8,
    });
    return;
  }

  const hasHttpResponse = Boolean(error?.response);
  const isNetworkError =
    !hasHttpResponse &&
    (
      !navigator.onLine ||
      error?.message === "Network Error" ||
      error?.code === "ERR_NETWORK" ||
      error?.response?.status === 0
    );

  if (isNetworkError) {
    const endpoint = error?.config?.url ?? "";
    const isLegalPublish =
      /\/(?:policies|documents)\/.+\/publish(?:\?|$)/.test(endpoint);
    notification.error({
      message: isLegalPublish ? "Publishing failed" : "Unable to reach server",
      description: isLegalPublish
        ? "The policy service did not return a response. Confirm the API server and CORS configuration, then try again."
        : "The server did not return a response. Check the API connection and try again.",
      duration: 6,
    });
    return;
  }

  // 401 — silent (axios interceptor handles token refresh / redirect)
  if (error?.response?.status === 401) return;

  // 440 — session expired modal (shared with query handler)
  if (
    error?.response?.status === 440 &&
    window.location.pathname !== "/login"
  ) {
    handleError(error);
    return;
  }

  // All other HTTP errors (403, 404, 422, 5xx) — show a descriptive toast
  notifyError(error);
};

export default handleError;
